/**
 * 가입 (공통/보안과개인정보 1-4 가입 흐름, 기능/08 1-1, 2026-11-02 대표님)
 * 1단계: 카카오, 네이버, 또는 아이디(아이디, 비밀번호, 이메일 + 인증번호)
 * 2단계: 사업자번호, 직업소개사업 등록번호, 등록증 사진, 가입 경로
 * 이미 가입된 사업자면 찾기로 안내하고, 간편 가입 중이면 아이디로 로그인해서 연결하게 한다.
 */
import { useState } from 'react';
import { api, rememberMethod, type Who } from '../api.ts';
import { Button, Check, Countdown, Field, Link, Message, Page, photoToDataUrl } from '../ui.tsx';

const SOURCES = ['아는 소장님 소개', '인터넷 검색(네이버, 구글)', '유튜브', '블로그나 카페', '협회', '일손이 영업(전화, 문자, 방문)', '그 밖'];

export interface SignupStart {
  method: '아이디' | '카카오' | '네이버';
  ticket?: string;
  login_id?: string;
  password?: string;
  email?: string;
  email_code_id?: string;
}

/** 1단계: 아이디로 가입할 때 아이디, 비밀번호, 이메일 인증 */
export function SignupStep1(props: { go: (p: string) => void; onReady: (s: SignupStart) => void }) {
  const [login_id, setId] = useState('');
  const [idNote, setIdNote] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<{ code_id: string; seconds: number; at: number; test_code?: string } | null>(null);
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  async function checkId() {
    if (!login_id) return;
    const r = await api('POST', '/api/signup/id-check', { login_id });
    setIdNote(r.ok ? '쓸 수 있는 아이디예요.' : r.message);
  }

  async function sendCode() {
    setError(null);
    setExpired(false);
    setVerified(false);
    const r = await api<{ code_id: string; seconds: number; test_code?: string }>('POST', '/api/signup/email/send', { email });
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setSent({ code_id: r.code_id, seconds: r.seconds, at: Date.now(), ...(r.test_code ? { test_code: r.test_code } : {}) });
  }

  async function checkCode() {
    if (!sent) return;
    const r = await api('POST', '/api/signup/email/check', { code_id: sent.code_id, code });
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setError(null);
    setVerified(true);
  }

  function social(slug: 'kakao' | 'naver') {
    window.location.href = `/api/auth/${slug}/start`;
  }

  return (
    <Page title="가입하기">
      <Button onClick={() => social('kakao')}>카카오로 가입</Button>
      <Button onClick={() => social('naver')}>네이버로 가입</Button>
      <h2 style={{ fontSize: 24 }}>아이디로 가입</h2>
      <Field label="아이디" value={login_id} onChange={(v) => { setId(v.toLowerCase()); setIdNote(null); }} onBlur={() => void checkId()} autoComplete="username" />
      <Message text={idNote} kind={idNote === '쓸 수 있는 아이디예요.' ? 'info' : 'error'} />
      <Field label="비밀번호" value={password} onChange={setPassword} type="password" autoComplete="new-password" />
      <Field label="이메일" value={email} onChange={(v) => { setEmail(v); setVerified(false); }} type="email" autoComplete="email" />
      <Button onClick={() => void sendCode()}>{sent ? '인증번호 다시 받기' : '인증번호 받기'}</Button>
      {sent && !verified && (
        <div>
          <Message text="인증번호를 보냈어요." kind="info" />
          {sent.test_code && <p>시험용 인증번호: {sent.test_code}</p>}
          {expired
            ? <Message text="시간이 지났어요. 인증번호를 다시 받아 주세요." />
            : <Countdown seconds={sent.seconds} startedAt={sent.at} onEnd={() => setExpired(true)} />}
          <Field label="인증번호" value={code} onChange={setCode} type="tel" />
          <Button onClick={() => void checkCode()} disabled={expired}>확인</Button>
        </div>
      )}
      {verified && <Message text="이메일을 확인했어요." kind="info" />}
      <Message text={error} />
      <Button
        primary
        disabled={!verified || !login_id || !password}
        onClick={() => sent && props.onReady({ method: '아이디', login_id, password, email, email_code_id: sent.code_id })}
      >
        다음
      </Button>
      <p><Link to="/login" go={props.go}>로그인으로 돌아가기</Link></p>
    </Page>
  );
}

/** 2단계: 사무소 확인 */
export function SignupStep2(props: {
  start: SignupStart;
  go: (p: string) => void;
  onSignedIn: (who: Who) => void;
  onLinkLogin: (ticket: string) => void;
}) {
  const [biz_no, setBiz] = useState('');
  const [license_no, setLicense] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [source, setSource] = useState('');
  const [other, setOther] = useState('');
  const [keep, setKeep] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taken, setTaken] = useState(false);
  const [busy, setBusy] = useState(false);
  const social = props.start.method !== '아이디';

  async function pick(file: File | undefined) {
    if (!file) return;
    try {
      setPhoto(await photoToDataUrl(file));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function submit() {
    setBusy(true);
    setError(null);
    const r = await api<{ who: Who }>('POST', '/api/signup', {
      ...props.start, biz_no, license_no, license_photo: photo, signup_source: source, signup_source_other: other, keep_login: keep
    });
    setBusy(false);
    if (r.ok) {
      rememberMethod(props.start.method);
      props.onSignedIn(r.who);
      return;
    }
    setError(r.message);
    setTaken(r.code === 'biz_taken');
  }

  return (
    <Page title="사무소 확인">
      <Field label="사업자등록번호" value={biz_no} onChange={setBiz} type="tel" />
      <Field label="직업소개사업 등록번호" value={license_no} onChange={setLicense} />
      <label style={{ display: 'block', margin: '0 0 16px' }}>
        <span style={{ display: 'block', marginBottom: 4 }}>직업소개사업 등록증 사진</span>
        <input type="file" accept="image/*,application/pdf,.pdf" onChange={(e) => void pick(e.target.files?.[0])} style={{ fontSize: 18 }} />
        {photo && <img src={photo} alt="등록증 사진" style={{ display: 'block', maxWidth: 240, marginTop: 8 }} />}
      </label>
      <fieldset style={{ border: 'none', padding: 0, margin: '0 0 16px' }}>
        <legend style={{ marginBottom: 4 }}>일손이를 어떻게 알게 되셨나요?</legend>
        {SOURCES.map((s) => (
          <label key={s} style={{ display: 'block' }}>
            <input type="radio" name="source" checked={source === s} onChange={() => setSource(s)} style={{ width: 20, height: 20, marginRight: 8 }} />
            {s}
          </label>
        ))}
        {source === '그 밖' && <Field label="직접 입력" value={other} onChange={setOther} />}
      </fieldset>
      <Check label="로그인 상태 유지" checked={keep} onChange={setKeep} />
      <Message text={error} />
      {taken && social && props.start.ticket && (
        <Button onClick={() => props.onLinkLogin(props.start.ticket ?? '')}>기존 아이디로 로그인</Button>
      )}
      {taken && !social && (
        <p>
          <Link to="/find/id" go={props.go}>아이디 찾기</Link>
          <Link to="/find/password" go={props.go}>비밀번호 찾기</Link>
        </p>
      )}
      <Button primary disabled={busy || !photo || !source} onClick={() => void submit()}>가입하기</Button>
    </Page>
  );
}
